import React, { useMemo, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import IconSpinner from '../IconSpinner/IconSpinner';

import css from './LineNotificationSettings.module.css';

const LINE_GREEN = '#06C755';
const DEFAULT_MAX_LINKS = 3;

const normalizeLineUserIds = value => {
  const source = Array.isArray(value) ? value : [];
  return source.reduce((collected, item) => {
    const normalized = String(item || '').trim();
    return normalized && !collected.includes(normalized) ? [...collected, normalized] : collected;
  }, []);
};

const normalizeLineLink = link => {
  const lineUserId = String(link?.lineUserId || link?.line_user_id || '').trim();
  if (!lineUserId) {
    return null;
  }

  return {
    lineUserId,
    lineDisplayName: link?.lineDisplayName || link?.line_display_name || null,
    linePictureUrl: link?.linePictureUrl || link?.line_picture_url || null,
    linkedAt: link?.linkedAt || link?.linked_at || null,
    deliveryStatus: link?.deliveryStatus || link?.delivery_status || null,
    lastDeliveryFailureAt:
      link?.lastDeliveryFailureAt || link?.last_delivery_failure_at || null,
    lastDeliveryFailureReason:
      link?.lastDeliveryFailureReason || link?.last_delivery_failure_reason || null,
  };
};

const normalizeLineLinks = value =>
  (Array.isArray(value) ? value : []).reduce((collected, item) => {
    const normalized = normalizeLineLink(item);
    return normalized ? [...collected, normalized] : collected;
  }, []);

export const maskLineUserId = lineUserId => {
  const normalized = String(lineUserId || '').trim();
  if (!normalized) {
    return '';
  }
  if (normalized.length <= 6) {
    return `${normalized.slice(0, 1)}...`;
  }
  return `${normalized.slice(0, 5)}...${normalized.slice(-2)}`;
};

const formatLinkedDate = linkedAt => {
  const parsed = Date.parse(linkedAt || '');
  if (!Number.isFinite(parsed)) {
    return null;
  }

  const date = new Date(parsed);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const LineIcon = () => (
  <span className={css.lineIcon} aria-hidden="true">
    LINE
  </span>
);

const getFallbackInitial = name => {
  const trimmed = String(name || '').trim();
  return trimmed ? trimmed.slice(0, 1).toUpperCase() : '?';
};

const ProfileAvatar = props => {
  const { accountName, pictureUrl, imageAlt } = props;
  const [imageFailed, setImageFailed] = useState(false);
  const shouldShowImage = pictureUrl && !imageFailed;

  return shouldShowImage ? (
    <img
      className={css.profileImage}
      src={pictureUrl}
      alt={imageAlt}
      referrerPolicy="no-referrer"
      onError={() => setImageFailed(true)}
    />
  ) : (
    <span className={css.profileFallback} aria-label={imageAlt} role="img">
      {getFallbackInitial(accountName)}
    </span>
  );
};

const StatusBadge = props => {
  const { loading, isLinked, count, maxLinks } = props;

  return (
    <span className={classNames(css.badge, isLinked ? css.badgeConnected : css.badgeMuted)}>
      <span
        className={css.badgeDot}
        style={{ backgroundColor: isLinked ? LINE_GREEN : undefined }}
        aria-hidden="true"
      />
      {loading ? (
        <FormattedMessage id="LineNotificationSettings.statusChecking" />
      ) : isLinked ? (
        <FormattedMessage
          id="LineNotificationSettings.statusConnected"
          values={{ count, maxLinks }}
        />
      ) : (
        <FormattedMessage id="LineNotificationSettings.statusNotConnected" />
      )}
    </span>
  );
};

const LineNotificationSettings = props => {
  const {
    lineUserIds: lineUserIdsProp = [],
    lineLinks: lineLinksProp = [],
    loading = false,
    error = null,
    actionError = null,
    successMessageId = null,
    maxLinkedAccounts = DEFAULT_MAX_LINKS,
    linkedAt = null,
    unlinkingLineUserId = null,
    onConnect,
    onDisconnect,
  } = props;
  const intl = useIntl();
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const lineLinks = useMemo(() => normalizeLineLinks(lineLinksProp), [lineLinksProp]);
  const lineUserIds = useMemo(() => {
    const explicitIds = normalizeLineUserIds(lineUserIdsProp);
    return explicitIds.length > 0
      ? explicitIds
      : normalizeLineUserIds(lineLinks.map(link => link.lineUserId));
  }, [lineLinks, lineUserIdsProp]);
  const lineAccounts = useMemo(
    () =>
      lineUserIds.map(lineUserId => ({
        lineUserId,
        ...(lineLinks.find(link => link?.lineUserId === lineUserId) || {}),
      })),
    [lineLinks, lineUserIds]
  );
  const count = lineUserIds.length;
  const maxLinks = Number(maxLinkedAccounts) > 0 ? Number(maxLinkedAccounts) : DEFAULT_MAX_LINKS;
  const isLinked = count > 0;
  const hasReconnectRequired = lineAccounts.some(
    account => account.deliveryStatus === 'needs_reconnect'
  );
  const canLinkMore = count < maxLinks;
  const canStartLineFlow = canLinkMore || hasReconnectRequired;

  return (
    <section
      className={css.card}
      aria-labelledby="line-notification-settings-heading"
      data-testid="line-notification-card"
    >
      <div className={css.header}>
        <div className={css.titleBlock}>
          <h2 id="line-notification-settings-heading" className={css.title}>
            <FormattedMessage id="LineNotificationSettings.title" />
          </h2>
          <p className={css.description}>
            {isLinked ? (
              <FormattedMessage
                id="LineNotificationSettings.connectedDescription"
                values={{ count }}
              />
            ) : (
              <FormattedMessage id="LineNotificationSettings.disconnectedDescription" />
            )}
          </p>
        </div>
        <StatusBadge loading={loading} isLinked={isLinked} count={count} maxLinks={maxLinks} />
      </div>

      {loading ? (
        <div className={css.loadingRow} role="status">
          <IconSpinner rootClassName={css.spinner} />
          <FormattedMessage id="LineNotificationSettings.loading" />
        </div>
      ) : null}

      {isLinked ? (
        <ul className={css.linkedList}>
          {lineAccounts.map(account => {
            const lineUserId = account.lineUserId;
            const disconnecting = unlinkingLineUserId === lineUserId;
            const linkedDate = formatLinkedDate(account.linkedAt || linkedAt);
            const accountName = account.lineDisplayName || account.displayName || null;
            const reconnectRequired = account.deliveryStatus === 'needs_reconnect';
            const displayName =
              accountName ||
              intl.formatMessage({
                id: 'LineNotificationSettings.fallbackProfileName',
              });
            const imageAlt = intl.formatMessage(
              { id: 'LineNotificationSettings.profileImageAlt' },
              { name: displayName }
            );
            return (
              <li
                key={lineUserId}
                className={classNames(css.linkedItem, reconnectRequired && css.linkedItemWarning)}
              >
                <div className={css.linkedAccountInfo}>
                  <ProfileAvatar
                    accountName={displayName}
                    pictureUrl={account.linePictureUrl}
                    imageAlt={imageAlt}
                  />
                  <div className={css.linkedText}>
                    <div className={css.linkedName} title={displayName}>
                      {accountName ? accountName : displayName}
                    </div>
                    <div className={css.linkedMeta}>
                      <FormattedMessage
                        id={
                          linkedDate
                            ? 'LineNotificationSettings.linkedAccountMetaWithDate'
                            : 'LineNotificationSettings.linkedAccountMeta'
                        }
                        values={{
                          lineUserId: maskLineUserId(lineUserId),
                          date: linkedDate,
                        }}
                      />
                    </div>
                    {reconnectRequired ? (
                      <div className={css.reconnectWarning}>
                        <FormattedMessage id="LineNotificationSettings.reconnectRequiredMeta" />
                      </div>
                    ) : null}
                  </div>
                </div>
                <button
                  type="button"
                  className={css.disconnectButton}
                  disabled={!!unlinkingLineUserId}
                  onClick={() => onDisconnect?.(lineUserId)}
                >
                  {disconnecting ? <IconSpinner rootClassName={css.buttonSpinner} /> : null}
                  <FormattedMessage id="LineNotificationSettings.disconnect" />
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      {successMessageId ? (
        <p className={css.success} role="status">
          <FormattedMessage id={successMessageId} />
        </p>
      ) : null}
      {error || actionError ? (
        <p className={css.error} role="alert">
          <FormattedMessage
            id={
              actionError
                ? 'LineNotificationSettings.actionFailed'
                : 'LineNotificationSettings.loadFailed'
            }
          />
        </p>
      ) : null}

      <div className={css.actions}>
        {canStartLineFlow ? (
          <button
            type="button"
            className={classNames(css.connectButton, isLinked && css.connectButtonSecondary)}
            onClick={onConnect}
            disabled={loading}
          >
            <LineIcon />
            {hasReconnectRequired ? (
              <FormattedMessage id="LineNotificationSettings.reconnect" />
            ) : isLinked ? (
              <FormattedMessage id="LineNotificationSettings.connectAnother" />
            ) : (
              <FormattedMessage id="LineNotificationSettings.connect" />
            )}
          </button>
        ) : null}
        {!canLinkMore && !hasReconnectRequired ? (
          <span className={css.maxLinks}>
            <FormattedMessage id="LineNotificationSettings.maxLinked" values={{ maxLinks }} />
          </span>
        ) : null}
      </div>

      <div className={css.howItWorks}>
        <button
          type="button"
          className={css.howItWorksButton}
          aria-expanded={howItWorksOpen}
          onClick={() => setHowItWorksOpen(current => !current)}
        >
          <FormattedMessage id="LineNotificationSettings.howItWorks" />
          <span className={classNames(css.chevron, howItWorksOpen && css.chevronOpen)} aria-hidden>
            ▾
          </span>
        </button>
        {howItWorksOpen ? (
          <div className={css.howItWorksContent}>
            <p>
              <FormattedMessage id="LineNotificationSettings.howItWorksConnect" />
            </p>
            <p>
              <FormattedMessage id="LineNotificationSettings.howItWorksDisconnect" />
            </p>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default LineNotificationSettings;
